import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { IsVarEmpty } from '../../../NonReact/utils/GlobalFunctions'

const ADVANCED_TRUNK_LABEL_TRUNKING = 'TRUNKING' as const
const ADVANCED_TRUNK_LABEL_BROADSOFT_CAPACITY = 'BROADSOFT_CAPACITY' as const
const ADVANCED_TRUNK_LABEL_WEBEX_CAPACITY = 'WEBEX_CAPACITY' as const

type AdvancedTrunkLabelStyle =
    | typeof ADVANCED_TRUNK_LABEL_TRUNKING
    | typeof ADVANCED_TRUNK_LABEL_BROADSOFT_CAPACITY
    | typeof ADVANCED_TRUNK_LABEL_WEBEX_CAPACITY

/**
 * How the synchronisation tab content is composed for a parent TelSys type.
 */
export enum PartitionModalSyncLayout {
    NONE = 'NONE',
    M6 = 'M6',
    BROADWORKS = 'BROADWORKS',
    WEBEX = 'WEBEX',
    TEAMS = 'TEAMS'
}

/**
 * Visibility and labelling flags for partition modal tabs derived from the parent TelSys type.
 */
export type PartitionModalTelSysLayout = {
    /** Synchronisation tab layout variant. */
    readonly syncLayout: PartitionModalSyncLayout
    /** Details: Tags field (after billing). */
    readonly detailsShowTags: boolean
    /** Details: "Licenced for" (moved from site settings for supported types). */
    readonly detailsShowLicensedFor: boolean
    /** Details: Billed monthly checkbox. */
    readonly detailsShowBilledMonthly: boolean
    /** Panasonic-only suffix on common-devices label. */
    readonly detailsCommonDevicesPanasonicSuffix: boolean
    /** Site: internal digit length. */
    readonly siteShowInternalDigitLength: boolean
    /** Site: country code ("Country Code Location"). */
    readonly siteShowCountryCode: boolean
    /** Site: E164 / recording playback checkbox. */
    readonly siteShowRecordingPlayback: boolean
    /** Advanced: trunk capacity fields; when false, advanced is CTI-only. */
    readonly advancedShowTrunking: boolean
    /** Advanced trunk field captions by product line. */
    readonly advancedTrunkLabelStyle: AdvancedTrunkLabelStyle
}

const layoutForKnownType = (telSysType: TelSysTypes): PartitionModalTelSysLayout => {
    switch (telSysType) {
        case TelSysTypes.SIEMENS_HIPATH_3000:
            return {
                syncLayout: PartitionModalSyncLayout.NONE,
                detailsShowTags: false,
                detailsShowLicensedFor: false,
                detailsShowBilledMonthly: false,
                detailsCommonDevicesPanasonicSuffix: false,
                siteShowInternalDigitLength: false,
                siteShowCountryCode: false,
                siteShowRecordingPlayback: true,
                advancedShowTrunking: false,
                advancedTrunkLabelStyle: ADVANCED_TRUNK_LABEL_TRUNKING
            }
        case TelSysTypes.PANASONIC_TDA_NCP:
            return {
                syncLayout: PartitionModalSyncLayout.NONE,
                detailsShowTags: true,
                detailsShowLicensedFor: false,
                detailsShowBilledMonthly: false,
                detailsCommonDevicesPanasonicSuffix: true,
                siteShowInternalDigitLength: false,
                siteShowCountryCode: false,
                siteShowRecordingPlayback: true,
                advancedShowTrunking: false,
                advancedTrunkLabelStyle: ADVANCED_TRUNK_LABEL_TRUNKING
            }
        case TelSysTypes.BROADSOFT_M6:
            return {
                syncLayout: PartitionModalSyncLayout.M6,
                detailsShowTags: true,
                detailsShowLicensedFor: true,
                detailsShowBilledMonthly: false,
                detailsCommonDevicesPanasonicSuffix: false,
                siteShowInternalDigitLength: true,
                siteShowCountryCode: false,
                siteShowRecordingPlayback: true,
                advancedShowTrunking: false,
                advancedTrunkLabelStyle: ADVANCED_TRUNK_LABEL_TRUNKING
            }
        case TelSysTypes.BROADSOFT_BROADWORKS:
            return {
                syncLayout: PartitionModalSyncLayout.BROADWORKS,
                detailsShowTags: true,
                detailsShowLicensedFor: true,
                detailsShowBilledMonthly: true,
                detailsCommonDevicesPanasonicSuffix: false,
                siteShowInternalDigitLength: true,
                siteShowCountryCode: true,
                siteShowRecordingPlayback: true,
                advancedShowTrunking: true,
                advancedTrunkLabelStyle: ADVANCED_TRUNK_LABEL_BROADSOFT_CAPACITY
            }
        case TelSysTypes.WEBEX_CALLING:
            return {
                syncLayout: PartitionModalSyncLayout.WEBEX,
                detailsShowTags: true,
                detailsShowLicensedFor: true,
                detailsShowBilledMonthly: true,
                detailsCommonDevicesPanasonicSuffix: false,
                siteShowInternalDigitLength: true,
                siteShowCountryCode: true,
                siteShowRecordingPlayback: true,
                advancedShowTrunking: false,
                advancedTrunkLabelStyle: ADVANCED_TRUNK_LABEL_WEBEX_CAPACITY
            }
        case TelSysTypes.MS_TEAMS:
            return {
                syncLayout: PartitionModalSyncLayout.TEAMS,
                detailsShowTags: true,
                detailsShowLicensedFor: true,
                detailsShowBilledMonthly: true,
                detailsCommonDevicesPanasonicSuffix: false,
                siteShowInternalDigitLength: true,
                siteShowCountryCode: true,
                siteShowRecordingPlayback: true,
                advancedShowTrunking: false,
                advancedTrunkLabelStyle: ADVANCED_TRUNK_LABEL_BROADSOFT_CAPACITY
            }
        default: // NOSONAR
            return layoutForKnownType(TelSysTypes.SIEMENS_HIPATH_3000)
    }
}

/**
 * Resolves partition modal field visibility for the parent telephony server type.
 *
 * @param parentTelSysType - Parsed `TelSysType` from the internal TelSys payload, if known.
 * @returns Layout flags; unknown or empty parent uses the conservative Siemens-style baseline.
 */
export const getPartitionModalTelSysLayout = (parentTelSysType: Nullable<TelSysTypes>): PartitionModalTelSysLayout => {
    if (IsVarEmpty(parentTelSysType)) {
        return layoutForKnownType(TelSysTypes.SIEMENS_HIPATH_3000)
    }

    return layoutForKnownType(parentTelSysType)
}
